/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { TimeUnit } from '../types';
import { EPOCH_DAYS, NANOS_OF_DAY, NANOS_OF_EPOCH } from './types';
import { NANOSECONDS_PER_DAY, NANOSECONDS_PER_MILLISECOND, NANOSECONDS_PER_SECOND, NANOSECONDS_PER_HOUR, NANOSECONDS_PER_MINUTE } from './time';

// Instant utilities

/* @internal */ export function getInstant(date: EPOCH_DAYS | 0n, time: NANOS_OF_DAY | NANOS_OF_EPOCH | 0n): NANOS_OF_EPOCH {
    return (BigInt(date) * NANOSECONDS_PER_DAY + time) as NANOS_OF_EPOCH;
}

/* @internal */ export interface InstantParts {
    milliseconds: number;
    nanoseconds: number;
}

/* @internal */ export const enum InstantPart {
    milliseconds,
    nanoseconds,
    all
}

/* @internal */ export function getInstantPart<P extends InstantPart>(ticks: NANOS_OF_EPOCH, part: P): P extends InstantPart.all ? InstantParts : number;
/* @internal */ export function getInstantPart(ticks: NANOS_OF_EPOCH, part: InstantPart) {
    const millisecondsPart = ticks / NANOSECONDS_PER_MILLISECOND;
    if (part === InstantPart.milliseconds) return Number(millisecondsPart);
    const nanoseconds = Number(ticks % NANOSECONDS_PER_MILLISECOND);
    if (part === InstantPart.nanoseconds) return nanoseconds;
    const milliseconds = Number(millisecondsPart);
    return { milliseconds, nanoseconds };
}

/* @internal */ export function getDateOfInstant(instant: NANOS_OF_EPOCH): EPOCH_DAYS {
    return Number((instant < 0 ? instant - (NANOSECONDS_PER_DAY - 1n) : instant) / NANOSECONDS_PER_DAY) as EPOCH_DAYS;
}

/* @internal */ export function getTimeOfInstant(instant: NANOS_OF_EPOCH): NANOS_OF_DAY {
    const time = instant < 0 ? instant % NANOSECONDS_PER_DAY + NANOSECONDS_PER_DAY : instant % NANOSECONDS_PER_DAY;
    return time as NANOS_OF_DAY;
}

/* @internal */ export function adjustInstant(instant: NANOS_OF_EPOCH, offsetSeconds: number): NANOS_OF_EPOCH {
    const offset = instant + BigInt(offsetSeconds) * NANOSECONDS_PER_SECOND;
    return offset as NANOS_OF_EPOCH;
}

/* @internal */ export function truncateInstant(ticks: NANOS_OF_EPOCH, precision: TimeUnit): NANOS_OF_EPOCH;
/* @internal */ export function truncateInstant(ticks: NANOS_OF_EPOCH, precision: TimeUnit) {
    switch (precision) {
        case "hour": return ticks / NANOSECONDS_PER_HOUR * NANOSECONDS_PER_HOUR;
        case "minute": return ticks / NANOSECONDS_PER_MINUTE * NANOSECONDS_PER_MINUTE;
        case "second": return ticks / NANOSECONDS_PER_SECOND * NANOSECONDS_PER_SECOND;
        case "millisecond": return ticks / NANOSECONDS_PER_MILLISECOND * NANOSECONDS_PER_MILLISECOND;
        case "nanosecond": return ticks;
    }
}
